import { useQuery, useMutation } from '@tanstack/react-query'
import { useRef, useEffect, useState, useMemo } from 'react'
import {
  getDataSyncOptions,
  getDisplayableEventsOptions,
  getNavigationOptions,
  pauseMutation,
  putGoToGarageMutation,
  reportFinishedMutation,
  reportStartedMutation,
  skipSegmentMutation,
  unpauseMutation,
} from '@/api/navigationApi/@tanstack/react-query.gen'
import { Client } from '@/api/main/client'
import { mean } from 'lodash-es'
import {
  DISPLAY_SIGN_POSTS,
  DISPLAY_DETECTIONS,
  NAVIGATION_REFETCH_INTERVAL_MS,
  SYNC_REFETCH_INTERVAL_MS,
  DISPLAYABLE_EVENTS_REFETCH_INTERVAL_MS,
} from '@/constants'

const GPS_NEGATIVE_LIMIT = 30
const GPS_POSITIVE_LIMIT = 5

export const useNavigationQueries = ({ client }: { client: Client | undefined; vehicleId?: string }) => {
  const [observations, setObservations] = useState<number[]>([])

  const syncQuery = useQuery({
    ...getDataSyncOptions({ client }),
    refetchInterval: SYNC_REFETCH_INTERVAL_MS,
    staleTime: SYNC_REFETCH_INTERVAL_MS,
  })

  const navigationQuery = useQuery({
    ...getNavigationOptions({ client }),
    refetchInterval: NAVIGATION_REFETCH_INTERVAL_MS,
    staleTime: NAVIGATION_REFETCH_INTERVAL_MS,
    retry: 2,
    enabled: syncQuery.data?.status === 'Waiting',
  })

  const detectionsQuery = useQuery({
    ...getDisplayableEventsOptions({ client }),
    refetchInterval: DISPLAYABLE_EVENTS_REFETCH_INTERVAL_MS,
    enabled:
      navigationQuery.data?.drive_data?.drive_id !== undefined &&
      (DISPLAY_DETECTIONS || DISPLAY_SIGN_POSTS) &&
      syncQuery.data?.status === 'Waiting',
  })

  // Compute nextRouteQueryKey directly from navigationQuery.data
  // This avoids circular dependency - no prop needed, no useEffect needed
  // Note: This key must match the structure in useDirectionsData for removeQueries to work
  const nextRouteQueryKeyRef = useRef<unknown[]>([])

  // 2. Update the observations whenever the vehicle moves (200ms)
  useEffect(() => {
    const confidence = navigationQuery.data?.patrol_vehicle?.position?.confidence

    // Guard against missing data
    if (confidence === undefined) return

    setObservations(prev => {
      const sizeLimit = Math.max(GPS_POSITIVE_LIMIT, GPS_NEGATIVE_LIMIT)
      // Immer-style update or standard spread to trigger React's change detection
      const next = [...prev, confidence]
      return next.length > sizeLimit ? next.slice(-sizeLimit) : next
    })
  }, [navigationQuery.data?.patrol_vehicle?.position?.confidence, setObservations])

  const isGPSAccuracyLow = useMemo(() => {
    if (observations.length < GPS_NEGATIVE_LIMIT) return false

    const negativeMean = mean(observations.slice(-GPS_NEGATIVE_LIMIT))
    const positiveMean = mean(observations.slice(-GPS_POSITIVE_LIMIT))

    const isBad = negativeMean < 0.5
    const isRecovering = observations.length > GPS_POSITIVE_LIMIT && positiveMean >= 0.5

    return isBad && !isRecovering
  }, [observations])

  const firstFutureSegmentId = navigationQuery.data?.future_street_segments[0]?.street_segment_id
  const patrolVehicle = navigationQuery.data?.patrol_vehicle

  useEffect(() => {
    const dropoffPoint = navigationQuery.data?.scheduled_shift?.dropoff_point
    const vehicle = patrolVehicle
    const originCoord =
      vehicle !== undefined && vehicle.position.latitude !== undefined && vehicle.position.longitude !== undefined
        ? { latitude: vehicle.position.latitude, longitude: vehicle.position.longitude }
        : undefined
    const originAzimuth =
      vehicle !== undefined && vehicle.position.latitude !== undefined && vehicle.position.longitude !== undefined
        ? vehicle.azimuth
        : undefined

    nextRouteQueryKeyRef.current = [
      'nextRoute',
      firstFutureSegmentId,
      dropoffPoint,
      originCoord?.latitude,
      originCoord?.longitude,
      originAzimuth,
    ]
  }, [firstFutureSegmentId, navigationQuery.data?.scheduled_shift?.dropoff_point, patrolVehicle])

  const { mutateAsync: start } = useMutation({
    ...reportStartedMutation({ client }),
  })

  const { mutateAsync: finish } = useMutation({
    ...reportFinishedMutation({ client }),
  })

  const { mutateAsync: pause } = useMutation({
    ...pauseMutation({ client }),
  })

  const { mutateAsync: resume } = useMutation({
    ...unpauseMutation({ client }),
  })

  const { mutateAsync: skip } = useMutation({
    ...skipSegmentMutation({ client }),
  })

  const { mutateAsync: goToGarage } = useMutation({
    ...putGoToGarageMutation({ client }),
  })

  return {
    syncQuery,
    navigationQuery,
    detectionsQuery,
    isGPSAccuracyLow,
    mutations: {
      start,
      finish,
      pause,
      resume,
      skip,
      goToGarage,
    },
  }
}
